import { useState } from 'react';
import { ExternalLink, Trash2 } from 'lucide-react';

const emptyText = 'No active Monday issues';

const getTicketTitle = (ticket) => (
  ticket.title ||
  ticket.ticketTitle ||
  ticket.externalTicketNumber ||
  ticket.externalTicketId ||
  `Ticket ${ticket.ticketId || ticket.id}`
);

const getTicketProvider = (ticket) => ticket.provider || ticket.status || ticket.queue || 'Support';

export default function LocationActivityPanel({
  activeIssues = [],
  ticketLinks = [],
  sectionError,
  isAdmin,
  createTicketLink,
  deleteTicketLink
}) {
  const [ticketForm, setTicketForm] = useState({ externalTicketId: '', externalTicketNumber: '' });
  const [actionError, setActionError] = useState('');

  const updateTicketField = (event) => {
    const { name, value } = event.target;
    setTicketForm(prev => Object.assign({}, prev, { [name]: value }));
  };

  const handleCreateTicketLink = async (event) => {
    event.preventDefault();
    if (!createTicketLink || !ticketForm.externalTicketId.trim()) return;
    setActionError('');
    try {
      await createTicketLink({
        provider: 'atera',
        externalTicketId: ticketForm.externalTicketId.trim(),
        externalTicketNumber: ticketForm.externalTicketNumber.trim()
      });
      setTicketForm({ externalTicketId: '', externalTicketNumber: '' });
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleDeleteTicketLink = async (linkId) => {
    if (!deleteTicketLink) return;
    setActionError('');
    try {
      await deleteTicketLink(linkId);
    } catch (error) {
      setActionError(error.message);
    }
  };

  return (
    <section id="activity" className="rounded-lg border border-white/10 bg-[#0b0b0b] p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Activity</h2>
          <p className="text-xs text-gray-500">Monday issues and linked support tickets</p>
        </div>
        <span className="rounded-full border border-white/10 px-2 py-1 text-xs font-semibold text-gray-400">
          {activeIssues.length} active
        </span>
      </div>

      {sectionError && (
        <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
          {sectionError}
        </div>
      )}

      {isAdmin && (
        <form onSubmit={handleCreateTicketLink} className="mb-4 grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 sm:grid-cols-[1fr_1fr_auto]">
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Atera Ticket ID
            <input
              name="externalTicketId"
              value={ticketForm.externalTicketId}
              onChange={updateTicketField}
              className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
            />
          </label>
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Ticket Number
            <input
              name="externalTicketNumber"
              value={ticketForm.externalTicketNumber}
              onChange={updateTicketField}
              className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={!ticketForm.externalTicketId.trim()}
              className="w-full rounded-lg bg-white px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Link Ticket
            </button>
          </div>
          {actionError && <p className="text-sm text-red-200 sm:col-span-3">{actionError}</p>}
        </form>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500">Active Monday Issues</h3>
          {activeIssues.length > 0 ? activeIssues.map(issue => (
            <div key={issue.id || issue.itemId || issue.name} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{issue.name || issue.title || 'Untitled issue'}</p>
                  <p className="mt-1 text-xs text-gray-500">{issue.boardName || issue.groupTitle || 'Monday'}</p>
                </div>
                {issue.status && (
                  <span className="shrink-0 rounded-full border border-white/10 px-2 py-1 text-[11px] font-semibold text-gray-300">
                    {issue.status}
                  </span>
                )}
              </div>
            </div>
          )) : (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm text-gray-500">{emptyText}</div>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500">Linked Tickets</h3>
          {ticketLinks.length > 0 ? ticketLinks.map(ticket => (
            <div key={ticket.id || ticket.ticketId || ticket.url || ticket.title} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{getTicketTitle(ticket)}</p>
                  <p className="mt-1 text-xs text-gray-500">{getTicketProvider(ticket)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {ticket.url && (
                    <a href={ticket.url} target="_blank" rel="noopener noreferrer" aria-label="Open ticket" className="text-gray-400 hover:text-white">
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  )}
                  {isAdmin && ticket.id && (
                    <button
                      type="button"
                      aria-label="Delete ticket link"
                      onClick={() => handleDeleteTicketLink(ticket.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-100"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )) : (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm text-gray-500">No linked tickets</div>
          )}
        </div>
      </div>
    </section>
  );
}
